// src/utils/leaseModel.js
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Put the template file in public/models/Office Lease Valuation Model 20250603.xlsx
const TEMPLATE_URL = "/models/Office Lease Valuation Model 20250603.xlsx";

/**
 * Make sure the DataTableColumns defined name exists and points at
 * the header row on the Data sheet.
 */
function ensureDataTableColumns(workbook) {
  const dn = workbook.definedNames;
  const model = dn.model || [];
  const hasDataTableColumns = model.some((n) => n.name === "DataTableColumns");

  if (!hasDataTableColumns) {
    const range = "Data!$A$1:$AK$1";
    try {
      dn.add("DataTableColumns", range);
      dn.model = [...model, { name: "DataTableColumns", ranges: [range] }];
    } catch (e) {
      console.error(
        "[LeaseModel] Failed to add DataTableColumns defined name",
        e
      );
    }
  }
}

// Explicit row → key mappings based on the master model
const FACILITY_ROW_KEY_MAP = {
  36: "cam", // Common area maintenance
  37: "furnished",
  38: "janitorial",
  39: "parking",
  40: "tax",
};

const UTILITY_ROW_KEY_MAP = {
  43: "electricity",
  44: "gas",
  45: "water",
  46: "phone",
  47: "internet",
};

const AMENITY_ROW_KEY_MAP = {
  50: "meeting",
  51: "kitchen",
  52: "copy",
  53: "receptionist",
};

function applyYNFlags(inputsSheet, rowKeyMap, selectedValues = []) {
  const set = new Set(selectedValues || []);

  Object.entries(rowKeyMap).forEach(([rowStr, key]) => {
    const row = Number(rowStr);
    const cell = inputsSheet.getCell(`C${row}`);
    cell.value = set.has(key) ? "Y" : "N";
  });
}

// Date helper for Excel: keep it as MM/DD/YYYY but avoid timezone issues.
function ensureMMDDYYYY(str) {
  if (!str) return "";

  // Already MM/DD/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    return str;
  }

  // Our app uses formatDateString → "YYYY-MM-DD"
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [yyyy, mm, dd] = str.split("-");
    return `${mm}/${dd}/${yyyy}`;
  }

  // Fallback: just return as-is
  return str;
}

/**
 * Fill the workbook's Inputs sheet with exhibit values.
 */
function fillWorkbookFromExhibit(workbook, exhibit = {}) {
  const {
    companyName,
    officeLeaseMarket,
    serviceProvider,
    locationAddress,
    city,
    state,
    zipcode,
    sqftLeased,
    startDate,
    term,
    propertyType,
    spaceType,
    settingType,
    desks,
    facility = [],
    utility = [],
    amenity = [],
  } = exhibit || {};

  const inputsSheet = workbook.getWorksheet("Inputs");
  if (!inputsSheet) {
    throw new Error("Inputs sheet not found in template.");
  }

  // Client name – Inputs!C4
  inputsSheet.getCell("C4").value = companyName || "";

  // Office Lease Market (optional, if you ever use it) – Inputs!C6
  if (officeLeaseMarket) {
    inputsSheet.getCell("C6").value = officeLeaseMarket;
  }

  // Subject property – Inputs!C10–C15
  inputsSheet.getCell("C10").value = serviceProvider || "";
  inputsSheet.getCell("C11").value = locationAddress || "";
  inputsSheet.getCell("C12").value = city || "";
  inputsSheet.getCell("C13").value = state || "";

  // Zip as number (drops leading zeros – change if you ever need to preserve them)
  if (zipcode) {
    const zipNum = Number(zipcode);
    inputsSheet.getCell("C14").value = Number.isNaN(zipNum) ? zipcode : zipNum;
  } else {
    inputsSheet.getCell("C14").value = "";
  }

  if (sqftLeased != null && sqftLeased !== "") {
    const sqftNum = Number(sqftLeased);
    inputsSheet.getCell("C15").value = Number.isNaN(sqftNum)
      ? sqftLeased
      : sqftNum;
  } else {
    inputsSheet.getCell("C15").value = "";
  }

  // Lease / description – Inputs!C20, C22, C25–C28
  inputsSheet.getCell("C20").value = ensureMMDDYYYY(startDate);

  if (term != null) {
    const termText = typeof term === "object" && term.label ? term.label : term;
    inputsSheet.getCell("C22").value = termText || "";
  } else {
    inputsSheet.getCell("C22").value = "";
  }

  if (propertyType != null) {
    inputsSheet.getCell("C25").value = propertyType || "";
  }
  if (spaceType != null) {
    inputsSheet.getCell("C26").value = spaceType || "";
  }
  if (settingType != null) {
    inputsSheet.getCell("C27").value = settingType || "";
  }

  if (desks != null && desks !== "") {
    const desksNum = Number(desks);
    inputsSheet.getCell("C28").value = Number.isNaN(desksNum)
      ? desks
      : desksNum;
  } else {
    inputsSheet.getCell("C28").value = "";
  }

  // Facilities / Utilities / Amenities
  applyYNFlags(inputsSheet, FACILITY_ROW_KEY_MAP, facility);
  applyYNFlags(inputsSheet, UTILITY_ROW_KEY_MAP, utility);
  applyYNFlags(inputsSheet, AMENITY_ROW_KEY_MAP, amenity);
}

/**
 * Internal helper: load template, ensure defined names, fill from exhibit.
 */
async function buildWorkbook(exhibit = {}) {
  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) {
    throw new Error(`Failed to fetch template (${res.status})`);
  }
  const arrayBuffer = await res.arrayBuffer();

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  ensureDataTableColumns(workbook);
  fillWorkbookFromExhibit(workbook, exhibit);

  return workbook;
}

/**
 * Existing behavior: fill the model and download it so you can inspect.
 */
export async function downloadTestLeaseModel(exhibit = {}) {
  try {
    const workbook = await buildWorkbook(exhibit);

    const outBuffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([outBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "ComplyOffice_Lease_Model_Test.xlsx");
  } catch (err) {
    console.error("[LeaseModel] downloadTestLeaseModel failed:", err);
  }
}

/**
 * New: build the filled workbook and return a BASE64 string so it can be
 * attached via EmailJS as a Variable Attachment.
 */
export async function generateLeaseModelBase64(exhibit = {}) {
  const workbook = await buildWorkbook(exhibit);
  const outBuffer = await workbook.xlsx.writeBuffer();

  // Convert ArrayBuffer → base64 (browser-safe)
  const bytes = new Uint8Array(outBuffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);

  return base64;
}
